import * as gm from "gammacv";
import {CBARCameraFacing, CBARCameraResolution, CBARContext, CBARDebug, CBARFeatureTracking} from "../../CBARContext";
import {CBARProcessNode} from "./CBARProcessNode";
import {CBARFrame} from "../CBARFrame";
import {canvasCreate, DeferredExecutor} from "../Utils";
import {CBARLineFinderNode} from "./CBARLineFinderNode";
import {CBARClassifierNode} from "./CBARClassifierNode";
import {CBServerConfig} from "../../../backend";
import {CBARPageFinderNode, NUM_CLUSTERS} from "./CBARPageFinderNode";
import CBARVideoCapture from "./CBARVideoCapture";
import {Rect, Size2d} from "mirada";
import {CBARFeatureTrackerNode} from "./CBARFeatureTrackerNode";
import {CBAR3DObjectTrackerNode} from "./CBAR3DObjectTrackerNode";
import {CBAREvent} from "../../CBARTypes";
import {RenderOptions} from "../../components";
import {CBARCameraPoseEstimator} from "./CBARCameraPoseEstimator";
import {CBARSurfaceSpawnerNode} from "./CBARSurfaceSpawnerNode";
import {CBARSegmentationNode} from "./CBARSegmentationNode";
import {CBAROpticalFlowNode} from "./CBAROpticalFlowNode";
import {imShow} from "../../CBARView";
import FastChannelExtractor from "./FastChannelExtractor";

export enum CBARPipelineTask {
    HoughLines,
    OpticalFlow
}

type TaskHandler<T> = (data:T)=>boolean

const EDGES_ENABLED = true;
const LINES_ENABLED = false;
const QUARTERING_ENABLED = false;

const MAX_COLOR_SIZE = 1024;
const MAX_PROCESSING_SIZE = 640;

// tslint:disable-next-line: no-any
type NodeConstructor<T> = new (pipeline:CBARPipeline) => T;

export class CBARPipeline {

    public pipeline:gm.InputType;
    public session?:gm.Session;
    public readonly stream:CBARVideoCapture;

    public readonly input:gm.MediaInput;

    public readonly pcLinesOutput?:gm.Tensor|null;

    public readonly colorOp?:gm.Operation|null;
    public readonly colorOutput?:gm.Tensor|null;

    public readonly greyscaleOp?:gm.Operation|null;
    public readonly greyscaleOutput?:gm.Tensor|null;

    public readonly quarteredOp?:gm.Operation|null;
    public readonly quarteredOutput?:gm.Tensor|null;

    public readonly edgesOp?:gm.Operation|null;
    public readonly edgesOutput?:gm.Tensor|null;

    public readonly colorSegmentationOp?:gm.Operation|null;
    public readonly colorSegmentationOutput?:gm.Tensor|null;

    private readonly inputWidth:number;
    private readonly inputHeight:number;

    public readonly processingWidth:number;
    public readonly processingHeight:number;


    constructor(public context:CBARContext,
                public config:CBServerConfig,
                protected readonly displayCanvas:HTMLCanvasElement,
                public readonly tracking:CBARFeatureTracking=CBARFeatureTracking.None) {

        this._nodes = [];

        this.inputWidth = Math.ceil(displayCanvas.width * window.devicePixelRatio);
        this.inputHeight = Math.ceil(displayCanvas.height * window.devicePixelRatio);

        //console.log(cv.getBuildInformation());

        const processingSize = Math.min(Math.min(displayCanvas.width, displayCanvas.height), MAX_PROCESSING_SIZE);
        const processingScale = this.inputWidth < this.inputHeight ? processingSize / this.inputWidth : processingSize / this.inputHeight;

        this.processingWidth = Math.ceil(this.inputWidth * processingScale);
        this.processingHeight = Math.ceil(this.inputHeight * processingScale);

        this.stream = new CBARVideoCapture();

        //this.input = new gm.Tensor('uint8', [this.inputHeight, this.inputWidth, 4]);
        this.input = new gm.MediaInput(this.displayCanvas, [this.inputHeight, this.inputWidth, 4]);

        this.pipeline = this.input;

        console.log(`Display size: ${this.displayCanvas.width} x ${this.displayCanvas.height}`);
        console.log(`Input size: ${this.inputWidth} x ${this.inputHeight}`);
        console.log(`Processing size: ${this.processingWidth} x ${this.processingHeight}`);
        console.log(`Video resolution: ${this.resolution}p`);

        // Display size: 375 x 667
        // hnl.mobileconsole.min.js:128 Input size: 750 x 1334
        // hnl.mobileconsole.min.js:128 Processing size: 216 x 384
        // hnl.mobileconsole.min.js:128 Output size: 216 x 384
        // hnl.mobileconsole.min.js:128 Video resolution: 1280p
        // hnl.mobileconsole.min.js:128 Processing size:767 x 431

        //getImageData took 9.90 going into (216, 384)

        if (this.tracking !== CBARFeatureTracking.None) {

            this._nodes.push(new CBAROpticalFlowNode(this));//must be first if things depend on it for tracking.

            const COLOR_SIZE = Math.max(Math.max(this.inputWidth, this.inputHeight), MAX_COLOR_SIZE);

            const colorScale = this.inputWidth > this.inputHeight ? COLOR_SIZE / this.inputWidth : COLOR_SIZE / this.inputHeight;

            this.colorOp = gm.resize(this.pipeline, Math.floor(colorScale * this.input.shape[1]), Math.floor(colorScale * this.input.shape[0]), 'bicubic');
            this.colorOutput = gm.tensorFrom(this.pipeline);
            //1334 x 750
            console.log(`color output size is ${this.colorOutput?.shape[1]} x ${this.colorOutput?.shape[0]}`)
            this.pipeline = this.colorOp;

            if (processingScale < 1) {
                this.pipeline = gm.resize(this.pipeline,  this.processingWidth, this.processingHeight, 'bicubic');
            }

            this.greyscaleOp = gm.grayscale(this.pipeline);
            this.greyscaleOutput = gm.tensorFrom(this.greyscaleOp);
            this.pipeline = this.greyscaleOp;

            if (QUARTERING_ENABLED) {
                const outputWidth = Math.ceil(this.processingWidth / 2);
                const outputHeight = Math.ceil(this.processingHeight / 2);

                //this.quarteredOp = gm.resize(this.greyscaleOp, this.outputWidth, this.outputHeight, 'bicubic');
                this.quarteredOp = FastChannelExtractor(this.greyscaleOp, outputWidth, outputHeight, 'bicubic');
                this.quarteredOutput = gm.tensorFrom(this.quarteredOp);
                this.pipeline = this.quarteredOp;

                console.log(`Output size: ${outputWidth} x ${outputHeight}`);
            }

            if (this.tracking === CBARFeatureTracking.Page || this.tracking === CBARFeatureTracking.Card) {
                this.colorSegmentationOp = gm.colorSegmentation(this.greyscaleOp, NUM_CLUSTERS);
                this.colorSegmentationOutput = gm.tensorFrom(this.colorSegmentationOp);
                this.pipeline = this.colorSegmentationOp;

                this._nodes.push(new CBARPageFinderNode(this));
            }

            if (this.tracking === CBARFeatureTracking.World) {

                if (EDGES_ENABLED || LINES_ENABLED) {
                    this.pipeline = gm.gaussianBlur(this.pipeline, 3, 9);
                    this.pipeline = gm.sobelOperator(this.pipeline);

                    this.edgesOp = gm.cannyEdges(this.pipeline, 0.15, 0.15);
                    this.edgesOutput = gm.tensorFrom(this.edgesOp);
                    this.pipeline = this.edgesOp;
                }

                if (LINES_ENABLED) {
                    //find lines. tune here: https://gammacv.com/examples/pc_lines
                    const layersCount = 2;
                    const dStep = 2;
                    const dCoeficient = 2.0;

                    this.pipeline = gm.pcLinesTransform(this.pipeline, dStep);
                    this.pipeline = gm.pcLinesEnhance(this.pipeline);

                    //reduce:
                    this.pipeline = gm.pcLinesReduceMax(this.pipeline, dCoeficient, 0);

                    for (let i = 0; i < layersCount; i += 1) {
                        this.pipeline = gm.pcLinesReduceMax(this.pipeline, dCoeficient, 1);
                    }

                    this.pcLinesOutput = gm.tensorFrom(this.pipeline);

                    this._nodes.push(new CBARLineFinderNode(this));
                }

                //this._nodes.push(new CBARCameraPoseEstimator(this));
                //this._nodes.push(new CBARFeatureTrackerNode(this));
                //this._nodes.push(new CBAR3DObjectTrackerNode(this));

                this._nodes.push(new CBARSurfaceSpawnerNode(this));
                this._nodes.push(new CBARSegmentationNode(this));
            }

            if (config.classifierPath && (this.tracking === CBARFeatureTracking.Classifier || this.tracking === CBARFeatureTracking.Face)) {
                this._nodes.push(new CBARClassifierNode(this, {
                    classifierPath:config.classifierPath,
                    maxRegions:config.classifierMaxRegions,
                    updateMS:config.classifierUpdateFrequency,
                    minSize:config.classifierMinSize ? new cv.Size(config.classifierMinSize[0], config.classifierMinSize[1]) : undefined,
                    maxSize:config.classifierMaxSize ? new cv.Size(config.classifierMaxSize[0], config.classifierMaxSize[1]) : undefined,
                }));
            }
        }

        this.session = new gm.Session()
        this.session.init(this.pipeline as gm.Operation);

        //need a separate session and pipeline per chain (yes this sucks)
        //the quarter op may be screwing up all past it
        //this.session.init(this.edgesOp as gm.Operation);
    }

    public get downsample() {
        return this.displayCanvas.width / this.processingWidth;
    }

    public getNode<T extends CBARProcessNode>(type: NodeConstructor<T>): T {
        let result = <T>this.nodes.find(e => e instanceof type);
        if (!result) {
            result = new type(this);
            this.nodes.push(result);
        }
        return result;
    }

    public captureRawOutput(roi?:Rect) {
        if (this.stream.resolution && this.stream.video) {
            const canvas = canvasCreate({width:this.stream.captureWidth, height:this.stream.captureHeight});
            console.log(`Capture size ${this.stream.captureWidth}, ${this.stream.captureHeight}`)
            const context = canvas.getContext("2d");
            if (context) {
                if (roi) {
                    canvas.width = roi.width;
                    canvas.height = roi.height;
                    context.drawImage(this.stream.video, roi.x, roi.y, roi.width, roi.height, 0, 0, canvas.width, canvas.height);
                } else {
                    context.drawImage(this.stream.video, 0, 0, this.stream.captureWidth, this.stream.captureHeight);
                }
                return canvas;
            }
        }
        return undefined;
    }

    public get nodes() {
        return this._nodes;
    }

    protected frameIndex = 0;
    public debugEnabled = true;

    protected tasks:DeferredExecutor<any>[] = []

    public requestTask<T>(task:CBARPipelineTask) {
        return new Promise<T>((resolve, reject) => {
            this.tasks[task] = {resolve, reject}
        });
    }

    private _subscriptions:{ [task: number]: TaskHandler<any>[]} = {};
    public subscribeToTask<T>(task:CBARPipelineTask, handler:TaskHandler<T>) {
        if (!this._subscriptions[task]) {
            this._subscriptions[task] = [];
        }
        this._subscriptions[task].push(handler);
    }

    public completeTask<T>(task:CBARPipelineTask, executor:()=>T) {
        const item = this.tasks[task];
        const subscriptions = this._subscriptions[task];
        if (item || subscriptions) {
            const result = executor();
            subscriptions?.forEach(handler=>handler(result));
            if (item) {
                item.resolve(result);
                delete this.tasks[task];
            }
        }
    }

    public refresh() {
        const activeNodes = this._nodes.filter(n=>n.enabled);
        activeNodes.forEach(n=>n.refresh());
    }

    public mirrorOutput = false;
    private _displayCount = 0;
    private _reportedError = false;

    public update(renderOptions:RenderOptions) {

        if (!this.session || !this.stream || !this.input) {
            return
        }

        //load in source image: //null means, ignore imageData;
        this.stream.processFrame(this.displayCanvas);

        const activeNodes = this._nodes.filter(n=>n.enabled);

        this._displayCount +=1;


        // try {

            const frame = new CBARFrame(this, this.frameIndex);
            this.frameIndex += 1;

            // if (this._displayCount % 60 == 0) {
            //     const debug = frame.greyscaleImage;
            //     console.count(`showing image ${debug.cols}x${debug.rows}`);
            //     imShow(debug);
            // }

            //process at each pipeline node

            activeNodes.forEach(n=>{
                n.update(frame, renderOptions);
                if (this.debugEnabled) {
                    n.debug(this.displayCanvas);
                }
            });

            frame.destroy();

        // } catch (error) {
        //
        //     if (!this._reportedError) {
        //         this._reportedError = true;
        //         console.error(`crashed after ${this._displayCount} frames.`, error);
        //     }
        //     this.context.stopVideoCamera();
        // }
    }

    public handleEvent(event: CBAREvent) {
        const activeNodes = this._nodes.filter(n=>n.enabled);
        activeNodes.forEach(n=>n.handleEvent(event));
    }

    public resolution = CBARCameraResolution.Default;
    public facing = CBARCameraFacing.Default;

    public start(resolution:CBARCameraResolution=CBARCameraResolution.Default, facing=CBARCameraFacing.Default) {

        if (facing === CBARCameraFacing.Default) {
            facing = this.tracking === CBARFeatureTracking.Face ? CBARCameraFacing.User : CBARCameraFacing.Environment;
        }

        if (this.tracking == CBARFeatureTracking.Page || this.tracking == CBARFeatureTracking.Card) {
            resolution = CBARCameraResolution.Maximum;
        }

        this.facing = facing;
        this.resolution = resolution;

        console.log("Feature tracking is set to '" + this.tracking + "' resolution: " + resolution);
        return this.stream.start(this.resolution, this.facing);
    }

    private readonly _nodes:CBARProcessNode[] = [];

    public get inputSize() : Size2d {
        return {width:this.displayCanvas.width, height:this.displayCanvas.height};
    }

    public release() {
        this._nodes.forEach(n=>n.destroy());

        this.stream.stop();
        this.session?.destroy();
        this.session = undefined;
    }

    public debugFeatureEnabled(feature:CBARDebug) {
        return feature === (this.config.debug & feature);
    }
}